import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, Play, X } from "lucide-react";

// To wire up a testimonial video, set `videoId` to the Cloudflare Stream video UID
// (and make sure CF_CUSTOMER_SUBDOMAIN below matches your account's subdomain).
// Leave `videoId` as null to keep the tile as a still image.
const CF_CUSTOMER_SUBDOMAIN = "customer-ebnkv8h0ztr1q1qy";

const testimonials = [
  {
    image: "/assets/testimonialsnew/klein.png",
    name: "Dr. Gary Klein, DDS",
    role: "Owner",
    practice: "Klein Family Dentistry",
    quote:
      "Asha has everything under one roof — 10 softwares and 20 AI agents. It's really like adding five or six people to my office. My team can finally do what they do best.",
    videoId: "5c3e0c4d4fa9279a17ec50bc5edbb838",
    accent: "from-blue-500 to-indigo-500",
    glow: "rgba(59,130,246,0.45)",
    objectPosition: "center 20%",
  },
  {
    image: "/assets/testimonialsnew/evans.png",
    name: "Dr. Jared Evans, DDS",
    role: "Owner",
    practice: "KiDDS Dentistry",
    quote:
      "When 60 outgoing calls can go out at once — all answered and handled — it's amazing how much calmer my front desk is. Asha has become a major stress reducer for my practice.",
    videoId: "dfab4e17e4042850b0b02583c264a7b8",
    accent: "from-sky-500 to-blue-500",
    glow: "rgba(14,165,233,0.45)",
    objectPosition: "center 20%",
  },
  {
    image: "/assets/testimonialsnew/cohn.png",
    name: "Dr. Nicole Cohn, DDS",
    role: "Owner",
    practice: "Hudson Valley Dental Arts",
    quote:
      "When you compare Asha to what you'd pay a body for a year of salary, it's incredible. Patients I hadn't seen in months are coming back in — we'd have never known who to contact otherwise.",
    videoId: "92d222dd6c2d8da47808c197608898cc",
    accent: "from-indigo-500 to-violet-500",
    glow: "rgba(99,102,241,0.45)",
    objectPosition: "center 20%",
  },
  {
    image: "/assets/testimonialsnew/belcher.png",
    name: "Carl Belcher",
    role: "Owner",
    practice: "Marquette Dentistry",
    quote:
      "The ROI-to-cost ratio has honestly been absurd. Asha captured 50 new patients my staff would have missed and brought us over $50K in return — in just a few months.",
    videoId: "a890d479cacf85f8bd3ffcf5a80b39f1",
    accent: "from-cyan-500 to-blue-500",
    glow: "rgba(6,182,212,0.45)",
    objectPosition: "center 20%",
  },
];

const TestimonialCard = ({ t, delay }) => {
  const [playing, setPlaying] = useState(false);
  const hasVideo = Boolean(t.videoId);
  const streamSrc = hasVideo
    ? `https://${CF_CUSTOMER_SUBDOMAIN}.cloudflarestream.com/${t.videoId}/iframe?autoplay=true&preload=auto&poster=${encodeURIComponent(
        window.location.origin + t.image,
      )}`
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="relative group h-[540px]"
    >
      {/* Soft outer glow on hover */}
      <div
        className="absolute -inset-1 rounded-[28px] opacity-0 group-hover:opacity-70 transition-opacity duration-500 blur-2xl pointer-events-none"
        style={{ background: t.glow }}
      />

      {/* Card body — full-bleed portrait or video */}
      <div className="relative h-full rounded-[24px] overflow-hidden shadow-[0_30px_80px_-24px_rgba(15,23,42,0.35)] border border-white/40">
        <AnimatePresence mode="wait">
          {playing && hasVideo ? (
            <motion.div
              key="video"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 bg-black"
            >
              <iframe
                src={streamSrc}
                title={`${t.name} testimonial`}
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
              />
              <button
                onClick={() => setPlaying(false)}
                className="absolute top-4 right-4 z-30 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-md border border-white/20 flex items-center justify-center text-white transition-colors"
                aria-label="Close video"
              >
                <X className="w-4 h-4" />
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="poster"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0"
            >
              {/* Portrait fills entire tile */}
              <img
                src={t.image}
                alt={t.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                style={{ objectPosition: t.objectPosition }}
              />

              {/* Frosted glass bottom panel */}
              <div className="absolute bottom-0 left-0 right-0 h-[62%] z-10 pointer-events-none">
                <div
                  className="absolute inset-0"
                  style={{
                    backdropFilter: "blur(18px) saturate(1.1)",
                    WebkitBackdropFilter: "blur(18px) saturate(1.1)",
                    maskImage:
                      "linear-gradient(to bottom, transparent 0%, transparent 22%, black 62%)",
                    WebkitMaskImage:
                      "linear-gradient(to bottom, transparent 0%, transparent 22%, black 62%)",
                  }}
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(to bottom, rgba(10,14,25,0) 0%, rgba(10,14,25,0.55) 45%, rgba(10,14,25,0.82) 75%, rgba(10,14,25,0.90) 100%)",
                  }}
                />
              </div>

              {/* Text content */}
              <div className="absolute bottom-0 left-0 right-0 z-20 px-6 pb-6 pt-4">
                <Quote className="w-5 h-5 text-white/40 mb-3" strokeWidth={2.5} />

                <motion.p
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: delay + 0.35, duration: 0.5 }}
                  className="text-[14px] leading-[1.55] text-white/95 font-medium mb-5"
                >
                  "{t.quote}"
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: delay + 0.45, duration: 0.5 }}
                  className="pt-4 border-t border-white/15 flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="font-bold text-white text-[16px] tracking-tight leading-snug truncate">
                      {t.name}
                    </div>
                    <div className="text-[13px] mt-1 text-white/65 truncate">
                      {t.role} · {t.practice}
                    </div>
                  </div>
                  {hasVideo && (
                    <button
                      onClick={() => setPlaying(true)}
                      className="relative shrink-0 group/play"
                      aria-label={`Play ${t.name} testimonial`}
                    >
                      <span
                        className="absolute inset-0 rounded-full border-2 border-white/50 animate-ping"
                        style={{ animationDuration: "2.5s" }}
                      />
                      <div className="relative w-[44px] h-[44px] rounded-full bg-white/95 backdrop-blur-md flex items-center justify-center shadow-[0_12px_30px_-10px_rgba(0,0,0,0.5)] transition-all duration-300 group-hover/play:scale-110 group-hover/play:bg-white">
                        <Play className="w-[16px] h-[16px] text-slate-900 ml-0.5 fill-slate-900" />
                      </div>
                    </button>
                  )}
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const OverviewTestimonialsSlide = ({ excludePractices = [] }) => {
  const visibleTestimonials = testimonials.filter(
    (t) => !excludePractices.includes(t.practice)
  );
  return (
    <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center p-8 xl:p-12 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs */}
      <div
        className="absolute top-[-220px] left-[-120px] w-[720px] h-[720px] rounded-full blur-[140px] opacity-30 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #bfdbfe 0%, #c4b5fd 50%, #93c5fd 100%)" }}
      />
      <div
        className="absolute bottom-[-220px] right-[-120px] w-[720px] h-[720px] rounded-full blur-[120px] opacity-30 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)" }}
      />
      {/* Dot grid */}
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="relative z-10 w-full max-w-[1360px] flex flex-col">
        {/* Header */}
        <div className="mb-8">
          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-[44px] lg:text-[54px] font-extrabold text-slate-900 tracking-tight leading-[1.05]"
          >
            But don't take our word for it.{" "}
            <span className="bg-gradient-to-r from-blue-600 via-indigo-500 to-violet-500 bg-clip-text text-transparent">
              See what Dentists have to say about Asha.
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="mt-4 text-[17px] lg:text-[18px] text-slate-500 leading-relaxed"
          >
            Practice owners are transforming their offices to run on autopilot with
            Asha's AI Operating System.
          </motion.p>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {visibleTestimonials.map((t, i) => (
            <TestimonialCard key={t.name} t={t} delay={0.4 + i * 0.12} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default OverviewTestimonialsSlide;
